import React, { useState } from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { ConfirmDialog, Field, inputCls } from '../components/ui/ConfirmDialog';
import { Section, ErrorPanel, LoadingRow, EmptyNote, UserLink, TableWrap, Th, Td } from '../components/ui/bits';
import { useSel, queryClient } from '../lib/queries';
import { supabase } from '../lib/supabase';
import { fmtDateTime } from '../lib/format';

export const ReferralsPage: React.FC = () => {
  const { data: refs, error, isLoading } = useSel<any[]>(['referrals'], () =>
    supabase.from('referrals').select('*').order('claimed_at', { ascending: false }).limit(100));
  const { data: abuse } = useSel<any[]>(['referral-abuse'], () =>
    supabase.from('referral_abuse_signals').select('*').limit(30));
  const { data: dist } = useSel<any[]>(['fraud-dist'], () =>
    supabase.from('referral_fraud_distribution').select('*'));
  const { data: cfgRows } = useSel<any[]>(['pipeline-config'], () =>
    supabase.from('pipeline_config').select('referral_rewards_live, referral_fraud_threshold').eq('id', 1));
  const cfg = cfgRows?.[0];

  const [release, setRelease] = useState<any>(null);
  const [thresholdOpen, setThresholdOpen] = useState(false);
  const [threshold, setThreshold] = useState('80');

  if (isLoading) return <LoadingRow />;
  if (error) return <ErrorPanel error={error} label="referrals" />;

  const rows = refs ?? [];
  const rewarded = rows.filter(r => r.rewarded_at).length;
  const held = rows.filter(r => r.flagged_at && !r.rewarded_at);

  return (
    <div>
      <PageHeader
        title="Referrals"
        subtitle="The one surviving referral system: rewards stack, anti-abuse is the completion bar plus pattern-based holds."
        actions={
          <Button size="sm" onClick={() => { setThreshold(String(cfg?.referral_fraud_threshold ?? 80)); setThresholdOpen(true); }}>
            Fraud threshold: {cfg?.referral_fraud_threshold ?? 80}
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatTile label="Referrals claimed" value={rows.length} accent="indigo" />
        <StatTile label="Rewarded" value={rewarded} accent="emerald" />
        <StatTile label="Held for review" value={held.length} accent={held.length ? 'amber' : 'stone'} />
        <StatTile label="Rewards switch" value={cfg?.referral_rewards_live ? 'On' : 'Off'} sub="toggle on the Email page" accent="stone" />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Section title="Referral ledger" sub="Newest 100. Shared campus wifi is expected; only patterns hold a payout.">
          {rows.length === 0 ? <EmptyNote>No referrals yet</EmptyNote> : (
            <TableWrap>
              <thead><tr><Th>Inviter</Th><Th>Invitee</Th><Th>Claimed</Th><Th>State</Th><Th></Th></tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}>
                    <Td><UserLink id={r.inviter_id} /></Td>
                    <Td><UserLink id={r.invitee_id} /></Td>
                    <Td className="whitespace-nowrap">{fmtDateTime(r.claimed_at)}</Td>
                    <Td>
                      {r.rewarded_at ? <Badge tone="emerald">Rewarded</Badge>
                        : r.flagged_at ? <Badge tone="rose">Held · score {r.fraud_score ?? '?'}</Badge>
                        : <Badge tone="amber">Awaiting completion bar</Badge>}
                    </Td>
                    <Td>{r.flagged_at && !r.rewarded_at && <Button size="sm" onClick={() => setRelease(r)}>Release</Button>}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>

        <div className="space-y-3">
          <Section title="Abuse signals by inviter">
            {(abuse ?? []).length === 0 ? <EmptyNote>Nothing flagged</EmptyNote> : (
              <TableWrap>
                <thead><tr><Th>Inviter</Th><Th>Total</Th><Th>Paid</Th><Th>Held</Th><Th>Worst score</Th><Th>Devices</Th><Th>IPs</Th></tr></thead>
                <tbody>
                  {(abuse ?? []).map((a, i) => (
                    <tr key={i}>
                      <Td><UserLink id={a.inviter_id} /></Td>
                      <Td className="tabular-nums">{a.referrals_total}</Td>
                      <Td className="tabular-nums">{a.referrals_paid}</Td>
                      <Td className="tabular-nums">{a.referrals_held}</Td>
                      <Td className="tabular-nums">{a.worst_score ?? ''}</Td>
                      <Td className="tabular-nums">{a.invitee_devices}</Td>
                      <Td className="tabular-nums">{a.invitee_ips}</Td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            )}
          </Section>
          <Section title="Fraud score distribution">
            {(dist ?? []).length === 0 ? <EmptyNote>No data</EmptyNote> : (
              <TableWrap>
                <thead><tr><Th>Band</Th><Th>Referrals</Th><Th>Paid</Th><Th>Held</Th></tr></thead>
                <tbody>
                  {(dist ?? []).map((d, i) => (
                    <tr key={i}>
                      <Td>{d.band}</Td>
                      <Td className="tabular-nums">{d.referrals}</Td>
                      <Td className="tabular-nums">{d.paid}</Td>
                      <Td className="tabular-nums">{d.held}</Td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            )}
          </Section>
        </div>
      </div>

      <ConfirmDialog
        open={!!release} onClose={() => setRelease(null)}
        title="Release this held payout" confirmLabel="Release"
        body={<>Clears the hold; the ten-minute sweep pays it on its next pass. The fraud score stays on record.</>}
        onConfirm={async () => {
          const { error: e } = await supabase.rpc('admin_release_referral_hold', { p_referral_id: release.id });
          if (e) throw new Error(e.message);
          queryClient.invalidateQueries({ queryKey: ['referrals'] });
        }}
      />

      <ConfirmDialog
        open={thresholdOpen} onClose={() => setThresholdOpen(false)}
        title="Set the fraud threshold" confirmLabel="Set"
        body={<>Referrals scoring at or above this are held for review instead of paying out.</>}
        onConfirm={async () => {
          const { error: e } = await supabase.rpc('admin_set_fraud_threshold', { p_value: Number(threshold) });
          if (e) throw new Error(e.message);
          queryClient.invalidateQueries({ queryKey: ['pipeline-config'] });
        }}
      >
        <Field label="Threshold (0 to 100)">
          <input type="number" min={0} max={100} value={threshold} onChange={e => setThreshold(e.target.value)} className={inputCls} />
        </Field>
      </ConfirmDialog>
    </div>
  );
};
